import styled from '@emotion/styled';
import { Button, Typography } from '@material-ui/core';
import { Link } from 'lucide-react';
import { useSnackbar } from 'notistack';
import React from 'react';
import { DadoLogoWithName } from '../../../components/DadoLogo';
import { useTranslation } from '../../../i18next';
import { FlexContainerVertical } from '../../../layout/Flex';
import { toFullName } from '../../../services/user';

const Container = styled('div')((p) => ({
  width: '100%',
  backgroundColor: p.theme.custom.colors.yellow.w300,
  padding: p.theme.spacing(3),
  position: 'relative',
  zIndex: 1000
}));

export const isImpersonation = (): boolean => {
  return !!window?.location.search.includes('impersonate=true');
};

export const getNonImpersonationUrl = (): string => {
  const u = new URL(window.location.href);
  u.searchParams.delete('impersonate');
  return u.toString();
};

export const ImpersonationBanner = ({ user }: { user: any }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const userName = user.preferredName || user.firstName || 'User';
  const fullName = toFullName(user);

  return (
    <Container>
      <FlexContainerVertical
        justifyContent="center"
        alignItems="center"
        fullWidth
        spacing={1}
      >
        <DadoLogoWithName size={null} variant="black" />
        <Typography variant="h3">
          {t(`You're impersonating {{ name }}`, { name: fullName })}
        </Typography>
        <Typography variant="body1">
          {t(
            `The screen below is {{ name }}'s Experience Hub. If you complete any tasks below, it's as if {{ name }} completed those tasks.`,
            { name: userName }
          )}
        </Typography>
        <Typography variant="body1">
          {t(
            `For a shareable link to {{ name }}'s Experience Hub that does not include this yellow banner, click the button below.`,
            { name: userName }
          )}
        </Typography>

        <Button
          variant="outlined"
          startIcon={<Link size={16} />}
          color="inherit"
          onClick={() => {
            const url = getNonImpersonationUrl();
            navigator.clipboard.writeText(url);
            enqueueSnackbar(t('Shareable link copied!'), {
              variant: 'success'
            });
          }}
        >
          {t('Get a link to share with {{ name }}', {
            name: userName
          })}
        </Button>
      </FlexContainerVertical>
    </Container>
  );
};

export const MaybeImpersonationBanner = ({
  user
}: {
  user: any;
}) => {
  const active = isImpersonation();
  return active ? <ImpersonationBanner user={user} /> : null;
};